/**
 * One site, opened from the map, without leaving the map.
 *
 * An operator working a map picks a site to see what is wrong with it and then
 * goes back to the map. This panel gives them the operational half of the site
 * dashboard — relays and their schedules, active alarms, open maintenance — in
 * the sidebar beside the map, with the same live data, the same permissions and
 * the same editing blades the dashboard itself uses. The full dashboard is one
 * button away for everything this panel deliberately leaves out.
 *
 * The panel is self-sufficient: it loads the site, joins its realtime room, and
 * provides the scope the dashboard cards read, because the map route stands at
 * customer scope and knows nothing about the site's group until the site has
 * loaded.
 *
 * Contains:
 * - MapSiteDetailPanel — the sidebar's site detail panel (default export)
 */
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useUser } from '@sophocles/app';
import ScopeContext, { useScope } from '@sophocles/app/ScopeContext';
import { selectSiteFeatureAssociationByKey } from '@sophocles/features/entities/site';
import { selectDisableThirdPartyApi } from '@sophocles/features/tenant/slice';
import { sitePath, siteAlarmHistoryPath, siteMaintenanceHistoryPath } from '@sophocles/router/paths';
import {
  buildSiteDetailParams,
  deriveSiteStatusWording,
} from '@sophocles/views/map/components/siteDetail/siteDetailDerivations';
import SiteDetailHeader from '@sophocles/views/map/components/siteDetail/SiteDetailHeader';
import SiteDetailRelays from '@sophocles/views/map/components/siteDetail/SiteDetailRelays';
import AlarmsSummaryCard from '@sophocles/views/sites/components/SiteDashboard/AlarmsSummaryCard';
import {
  deriveSiteCardStatus,
  filterVisibleAlarms,
  getAlarmStatus,
  sortRelays,
} from '@sophocles/views/sites/components/SiteDashboard/dashboardDerivations';
import MaintenanceBlade from '@sophocles/views/sites/components/SiteDashboard/MaintenanceBlade';
import MaintenanceSummaryCard from '@sophocles/views/sites/components/SiteDashboard/MaintenanceSummaryCard';
import RelayBlade from '@sophocles/views/sites/components/SiteDashboard/RelayBlade';
import useDashboardBlades from '@sophocles/views/sites/hooks/useDashboardBlades';
import useSiteDashboardData from '@sophocles/views/sites/hooks/useSiteDashboardData';
import useSiteViewPreferences from '@sophocles/views/sites/hooks/useSiteViewPreferences';

// Placeholder card heights while the snapshot loads — the two summary cards
// then a relay card, in body order, so the panel doesn't reflow when the real
// ones arrive.
const SKELETON_CARD_HEIGHTS_PX = [160, 140, 260];

/**
 * @param {object} props
 * @param {string|number} props.siteId - the site to show.
 * @param {string|number} props.customerId - the customer whose map is open.
 * @param {Function} props.onBack - close the panel and return to the site list.
 * @param {Function} props.onFindOnMap - centre the map on this site (siteId).
 */
export default function MapSiteDetailPanel(props) {
  // Picking a second site starts the panel over rather than carrying the first
  // site's open blade across: the realtime subscription and the loads rebuild
  // on the site id either way, and a schedule editor left open over a different
  // site is the one piece of state that cannot honestly follow it.
  return <SiteDetailPanel key={props.siteId} {...props} />;
}

MapSiteDetailPanel.propTypes = {
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

/**
 * @param {object} props
 * @param {string|number} props.siteId - the site to show.
 * @param {string|number} props.customerId - the customer whose map is open.
 * @param {Function} props.onBack - close the panel and return to the site list.
 * @param {Function} props.onFindOnMap - centre the map on this site (siteId).
 */
function SiteDetailPanel({ siteId, customerId, onBack, onFindOnMap }) {
  const { t } = useTranslation();
  const { customer: scopeCustomer } = useScope();
  const { user } = useUser();

  // The map route has no group segment, so the hook derives the site's primary
  // group from the snapshot once it lands.
  const routeParams = useMemo(() => ({ cust_id: customerId, site_id: siteId }), [customerId, siteId]);
  const {
    customer,
    loading,
    dashboardError,
    unit,
    site,
    alarms,
    capabilities,
    sitePermissions,
    curfew,
    alarmDefs,
    refetch,
  } = useSiteDashboardData(siteId, routeParams);

  // Seeds the structure slice's schedule-view preference, which RelayCard reads
  // to choose between the schedule list and the timeline.
  useSiteViewPreferences();
  const blades = useDashboardBlades();

  const disableThirdPartyApi = useSelector(selectDisableThirdPartyApi);
  const calendarFeature = useSelector(selectSiteFeatureAssociationByKey(siteId ?? 0, 'CALENDAR'));
  const usesCalendar = calendarFeature?.is_effectively_enabled ?? false;

  // Per-(user,site) gates: the per-site block from meta.permissions wins over
  // the global capability of the same name. Identical to the dashboard's merge.
  const siteCapabilities = sitePermissions ? { ...capabilities, ...sitePermissions } : capabilities;
  const canManageUnit = siteCapabilities.can_manage_unit ?? false;

  // Admin-only alarm visibility follows the global gate, not the per-site
  // block — same fallback chain the dashboard uses.
  const isStaff = capabilities.is_staff ?? false;
  const isSuperUser = user.get('is_superuser', false);
  const canViewAdminAlarms = capabilities.can_view_admin_alarms ?? (isStaff || isSuperUser);
  const visibleAlarms = useMemo(
    () => filterVisibleAlarms(alarms, alarmDefs, canViewAdminAlarms),
    [alarms, alarmDefs, canViewAdminAlarms],
  );
  const alarmStatus = useMemo(() => getAlarmStatus(visibleAlarms, alarmDefs), [visibleAlarms, alarmDefs]);
  const relays = useMemo(() => sortRelays(unit?.relays), [unit]);

  const params = buildSiteDetailParams(site, customerId, siteId);
  const scope = useMemo(
    () => ({ customer: scopeCustomer ?? null, siteGroup: site?.owner ?? null, site: site ?? null, unit: unit ?? null }),
    [scopeCustomer, site, unit],
  );

  const handleFindOnMap = useCallback(() => onFindOnMap(siteId), [onFindOnMap, siteId]);

  // The group the panel builds every path and blade from comes from the loaded
  // snapshot, so there is nothing to render until it has.
  if (site == null || params == null) {
    return (
      <PanelFrame>
        {dashboardError ? (
          <SiteDetailUnavailable onBack={onBack} onRetry={refetch} />
        ) : (
          <Stack spacing={2} sx={{ p: 'var(--space-2)' }}>
            {SKELETON_CARD_HEIGHTS_PX.map(height => (
              <Skeleton key={`site-detail-skeleton-${height}`} variant="rounded" height={height} />
            ))}
          </Stack>
        )}
      </PanelFrame>
    );
  }

  const statusInputs = {
    hasDevice: unit != null,
    hasRadio: unit?.radio != null,
    hasEverReported: unit?.last_active != null,
    isRadioOnline: unit?.radio?.online ?? null,
    alarmCount: visibleAlarms.length,
  };

  return (
    <ScopeContext.Provider value={scope}>
      <PanelFrame>
        <SiteDetailHeader
          siteName={site.name ?? ''}
          siteGroupName={site.owner?.name ?? null}
          status={deriveSiteCardStatus({ ...statusInputs, alarmStatus })}
          statusWording={deriveSiteStatusWording(statusInputs)}
          deactivated={site.deactivated ?? false}
          onBack={onBack}
          onFindOnMap={handleFindOnMap}
          dashboardHref={sitePath(params)}
        />

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', p: 'var(--space-2)' }}>
          {dashboardError ? (
            <Alert
              severity="error"
              sx={{ mb: 1 }}
              action={
                <Button size="small" color="inherit" onClick={() => refetch()}>
                  {t('actions.retry', 'Retry')}
                </Button>
              }
            >
              {t('sites.dashboardLoadError', "Couldn't load this site. Check your connection and try again.")}
            </Alert>
          ) : null}

          {/* What is wrong with the site comes before what it is doing: an
              operator opens a site from the map to find the problem. */}
          <Stack spacing={2}>
            <AlarmsSummaryCard
              alarms={visibleAlarms}
              relays={relays}
              siteId={siteId}
              capabilities={siteCapabilities}
              historyPath={siteAlarmHistoryPath(params)}
            />
            <MaintenanceSummaryCard
              siteId={siteId}
              onOpen={blades.openMaintenance}
              onAdd={blades.handleAddMaintenance}
              capabilities={siteCapabilities}
              historyPath={siteMaintenanceHistoryPath(params)}
            />

            <SiteDetailRelays
              loading={loading}
              siteId={siteId}
              unit={unit}
              relays={relays}
              siteTimeZone={site.time_zone ?? 'UTC'}
              customer={customer}
              params={params}
              usesCalendar={usesCalendar}
              isBillboard={site.is_billboard ?? false}
              hasCharting={customer.getIn(['options', 'charting_option'], 0) > 0 && !disableThirdPartyApi}
              curfew={curfew}
              siteIccid={unit?.radio?.iccid ?? null}
              nextSunriseRaw={site.next_sunrise ?? null}
              nextSunsetRaw={site.next_sunset ?? null}
              onClickEditSchedule={blades.openRelayBlade}
              canManageUnit={canManageUnit}
            />
          </Stack>
        </Box>

        <RelayBlade
          customerId={params.cust_id}
          siteId={params.site_id}
          siteGroupId={params.sitegroup_id}
          open={blades.isRelayBladeOpen}
          initialRelayId={blades.relayBladeParams.initialRelayId}
          initialScheduleId={blades.relayBladeParams.initialScheduleId}
          initialTab={blades.relayBladeParams.initialTab}
          onClose={blades.closeRelayBlade}
          siteData={site}
          isBillboardOverride={site.is_billboard ?? false}
        />

        <MaintenanceBlade
          open={blades.maintenanceOpen}
          onClose={blades.closeMaintenance}
          siteId={siteId}
          maintenancePath={siteMaintenanceHistoryPath(params)}
          startAddingToken={blades.maintenanceAddToken}
          focusedLogId={blades.maintenanceFocusedLogId}
        />
      </PanelFrame>
    </ScopeContext.Provider>
  );
}

SiteDetailPanel.propTypes = {
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onBack: PropTypes.func.isRequired,
  onFindOnMap: PropTypes.func.isRequired,
};

/**
 * The panel's column: a fixed header over a scrolling body, filling whatever
 * the sidebar gives it.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
function PanelFrame({ children }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        bgcolor: 'var(--surface-secondary)',
      }}
    >
      {children}
    </Box>
  );
}

/**
 * What the panel shows when the site cannot be loaded — most often because the
 * viewer may not see it. It names no detail of the site it could not open.
 *
 * @param {object} props
 * @param {Function} props.onBack - return to the site list.
 * @param {Function} props.onRetry - try the load again.
 */
function SiteDetailUnavailable({ onBack, onRetry }) {
  const { t } = useTranslation();

  return (
    <Stack spacing={2} sx={{ p: 'var(--space-3)', alignItems: 'flex-start' }}>
      <Alert severity="warning" sx={{ width: '100%' }}>
        {t('map.siteUnavailable', "This site isn't available to you, or no longer exists.")}
      </Alert>
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="outlined" onClick={onBack}>
          {t('actions.back', 'Back')}
        </Button>
        <Button size="small" onClick={() => onRetry()}>
          {t('actions.retry', 'Retry')}
        </Button>
      </Stack>
    </Stack>
  );
}

PanelFrame.propTypes = { children: PropTypes.node };

SiteDetailUnavailable.propTypes = {
  onBack: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
};
